import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import Dot from '../Dot';
import './LoginBanner.css';

const loginPageImages = [
  {
    image: '/images/slideshow/image1.jpg',
    alt: 'slideshow.slide1Desc',
    title: 'slideshow.title',
    description: 'slideshow.slide1Desc',
  },
  {
    image: '/images/slideshow/image2.jpg',
    alt: 'slideshow.slide2Desc',
    title: 'slideshow.title',
    description: 'slideshow.slide2Desc',
  },
  {
    image: '/images/slideshow/image3.jpg',
    alt: 'slideshow.slide3Desc',
    title: 'slideshow.title',
    description: 'slideshow.slide3Desc',
  },
  {
    image: '/images/slideshow/image4.jpg',
    alt: 'slideshow.slide4Desc',
    title: 'slideshow.title',
    description: 'slideshow.slide4Desc',
  },
];

const BannerSlide = ({ slideData, index, imgStyle }) => {
  const { t } = useI18n();

  return (
    <div className="login-banner-slide" id={`slide-${index + 1}`}>
      <img
        src={slideData.image}
        alt={t(slideData.alt)}
        className="login-banner-image"
        style={imgStyle}
        {...(index > 0 ? { loading: 'lazy' } : {})}
      />
      <div className="login-banner-text-wrapper login-banner-text-wrapper--bottom">
        <div className="login-banner-title login-banner-title--medium">{t(slideData.title)}</div>
        <div className="login-banner-description">{t(slideData.description)}</div>
      </div>
    </div>
  );
};

BannerSlide.propTypes = {
  slideData: PropTypes.shape({
    image: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  imgStyle: PropTypes.object.isRequired,
};

const LoginBanner = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [translateValue, setTranslateValue] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const imageSliderInterval = useRef(null);
  const carouselSlideRef = useRef(null);
  const currentIndexRef = useRef(0);

  const getSlideWidth = () => {
    return carouselSlideRef.current ? carouselSlideRef.current.clientWidth : 0;
  };

  const startInterval = () => {
    if (!isPaused && imageSliderInterval.current === null) {
      imageSliderInterval.current = setInterval(goToSlide, 4000);
    }
  };

  const goToSlide = () => {
    setCurrentIndex(prevIndex => {
      const nextIndex = prevIndex === loginPageImages.length - 1 ? 0 : prevIndex + 1;
      currentIndexRef.current = nextIndex;
      setTranslateValue(-(getSlideWidth() * nextIndex));
      return nextIndex;
    });
  };

  const clearImageSliderInterval = () => {
    if (imageSliderInterval.current !== null) {
      clearInterval(imageSliderInterval.current);
      imageSliderInterval.current = null;
    }
  };

  // Set up the auto-slide interval
  useEffect(() => {
    startInterval();

    return () => {
      clearImageSliderInterval();
    };
  }, [isPaused]); // Restart interval when pause state changes

  // Recalculate slide position and image height when the window is resized
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setTranslateValue(-(getSlideWidth() * currentIndexRef.current));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDotClick = (index) => {
    // Clear existing interval and restart it to reset the timer
    clearImageSliderInterval();

    currentIndexRef.current = index;
    setCurrentIndex(index);
    setTranslateValue(-(getSlideWidth() * index));

    // Restart the interval
    startInterval();
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
    clearImageSliderInterval();
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // Dynamic styles that need to be computed at runtime
  const carouselSlideStyle = {
    transform: `translateX(${translateValue}px)`,
  };

  const imgStyle = {
    height: windowHeight,
  };

  return (
    <div
      className="login-banner"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="login-banner-carousel-container">
        <div
          className="login-banner-carousel-slide"
          style={carouselSlideStyle}
          ref={carouselSlideRef}
        >
          {loginPageImages.map((slideData, index) => (
            <BannerSlide
              key={index}
              slideData={slideData}
              index={index}
              imgStyle={imgStyle}
            />
          ))}
        </div>
      </div>
      <div className="login-banner-dots">
        {
          loginPageImages.map((slideData, index) => (
            <Dot
              key={index}
              dotIndex={index}
              dotSize={6}
              currentIndex={currentIndex}
              onDotClick={handleDotClick}
            />
          ))
        }
      </div>
    </div>
  );
};

export default LoginBanner;
